import {
  BackupOutlinedIcon,
  DesktopWindowsOutlinedIcon,
  LocalGasStationOutlinedIcon,
  PhoneAndroidOutlinedIcon,
  PhoneIphoneOutlinedIcon,
  SmsOutlinedIcon,
  StoreOutlinedIcon,
  CheckboxItem,
  ButtonItem,
} from '@occc-app/design-system';
import {
  AudienceTimeSinceLastVisitTargetingAttribute,
  Category_Restrictions,
  Fuel_And_Convenience_Profile_Operator,
  Time_Period,
  Time_Since_Last_Visit_Operator,
  Audience_Operator,
  Point_Balance_Target,
  Time_Since_Membership_Acquisition_Operator,
  Retailer_Network_User_Type,
  Retailer_Network_Operator,
  Communication_Preference_Mechanism,
  Communication_Preference_Opt_In_Status,
} from '@occc/graphql';

import {
  ACQUISITION_CHANNEL_VALUE,
  AcquisitionChannelItem,
  FuelAndConvenienceProfileValues,
} from './types';
import { MembershipCreatedValues, PointBalanceOperatorValues, PointBalanceValues } from '../types';

export const acquisitionChannelItems: AcquisitionChannelItem[] = [
  {
    label: 'Pump',
    value: ACQUISITION_CHANNEL_VALUE.Pump,
    icon: LocalGasStationOutlinedIcon,
  },
  {
    label: 'POS',
    value: ACQUISITION_CHANNEL_VALUE.POS,
    icon: StoreOutlinedIcon,
  },
  {
    label: 'iOS Native',
    value: ACQUISITION_CHANNEL_VALUE.IOS,
    icon: PhoneIphoneOutlinedIcon,
  },
  {
    label: 'Android Native',
    value: ACQUISITION_CHANNEL_VALUE.Android,
    icon: PhoneAndroidOutlinedIcon,
  },
  {
    label: 'Imported',
    value: ACQUISITION_CHANNEL_VALUE.Imported,
    icon: BackupOutlinedIcon,
  },
  {
    label: 'Web',
    value: ACQUISITION_CHANNEL_VALUE.Web,
    icon: DesktopWindowsOutlinedIcon,
  },
  {
    label: 'SMS',
    value: ACQUISITION_CHANNEL_VALUE.Sms,
    icon: SmsOutlinedIcon,
  },
];

export const eaivAttributes: CheckboxItem[] = [
  { label: 'Age verified', value: Category_Restrictions.Age },
  {
    label: 'EAIV verified',
    value: Category_Restrictions.Eaiv,
  },
];

export const verifiedItems: ButtonItem[] = [
  {
    title: 'Member Is',
    value: Fuel_And_Convenience_Profile_Operator.IsIn,
  },
  {
    title: 'Member Is Not',
    value: Fuel_And_Convenience_Profile_Operator.IsNotIn,
  },
];

export const lastVisitOperatorsOptions: [
  {
    label: string;
    value: '';
  },
  ...{
    label: string;
    value: Time_Since_Last_Visit_Operator;
  }[],
] = [
  { label: 'Clear Selection', value: '' },
  { label: 'Exactly', value: Time_Since_Last_Visit_Operator.IsEqualTo },
  { label: 'More than', value: Time_Since_Last_Visit_Operator.IsGreaterThan },
  {
    label: 'More or equal to',
    value: Time_Since_Last_Visit_Operator.IsGreaterThanOrEqualTo,
  },
  { label: 'Less than', value: Time_Since_Last_Visit_Operator.IsLessThan },
  { label: 'Less or equal to', value: Time_Since_Last_Visit_Operator.IsLessThanOrEqualTo },
];

export const enrollmentStatusOptions: [
  {
    label: string;
    value: string;
  },
  ...{
    label: string;
    value: NonNullable<FuelAndConvenienceProfileValues>['hasACH'];
  }[],
] = [
  { label: 'Clear Selection', value: '' },
  { label: 'Enrolled', value: 'true' },
  { label: 'Not enrolled', value: 'false' },
];

export const optInStatusOptions: {
  label: string;
  value: Communication_Preference_Opt_In_Status | '';
}[] = [
  { label: 'Clear Selection', value: '' },
  { label: 'Opted In', value: Communication_Preference_Opt_In_Status.OptedIn },
  { label: 'Opted Out', value: Communication_Preference_Opt_In_Status.OptedOut },
  { label: 'No Opt In Set', value: Communication_Preference_Opt_In_Status.NotSet },
];

export const optInMechanismToTitleMap: Record<Communication_Preference_Mechanism, string> = {
  [Communication_Preference_Mechanism.Sms]: 'SMS Opt In Status',
  [Communication_Preference_Mechanism.Push]: 'Push Opt In Status',
};

export const optInStatusToTitleMap: Record<Communication_Preference_Opt_In_Status, string> = {
  [Communication_Preference_Opt_In_Status.OptedIn]: 'Opted In',
  [Communication_Preference_Opt_In_Status.OptedOut]: 'Opted Out',
  [Communication_Preference_Opt_In_Status.NotSet]: 'No Opt In Set',
};

export const lastVisitTimePeriodOptions: [
  {
    label: string;
    value: '';
  },
  ...{
    label: string;
    value: AudienceTimeSinceLastVisitTargetingAttribute['period'];
  }[],
] = [
  { label: 'Clear Selection', value: '' },
  { label: 'day', value: Time_Period.Day },
  { label: 'week', value: Time_Period.Week },
  { label: 'month', value: Time_Period.Month },
];

export const pointBalanceOperatorOptions: {
  label: string;
  value: PointBalanceOperatorValues;
}[] = [
  { label: 'Clear Selection', value: '' },
  { label: 'Exactly', value: Audience_Operator.IsEqualTo },
  { label: 'More than', value: Audience_Operator.IsGreaterThan },
  { label: 'More than or equal to', value: Audience_Operator.IsGreaterThanOrEqualTo },
  { label: 'Less than', value: Audience_Operator.IsLessThan },
  { label: 'Less than or equal to', value: Audience_Operator.IsLessThanOrEqualTo },
  { label: 'Between (and including)', value: 'BETWEEN' },
];

export const pointTargetingPeriodOptions: { label: string; value: Point_Balance_Target | '' }[] = [
  { label: 'Clear Selection', value: '' },
  { label: 'Ever', value: Point_Balance_Target.Total },
  { label: 'Currently', value: Point_Balance_Target.Current },
];

export const pointBalanceInitialFormValues: PointBalanceValues = {
  operator: '',
  points: null,
  period: '',
};

export const pointBalanceOperatorToTitleMap: Record<Audience_Operator, string> = {
  [Audience_Operator.IsEqualTo]: '',
  [Audience_Operator.IsGreaterThan]: 'More than',
  [Audience_Operator.IsGreaterThanOrEqualTo]: 'More than or',
  [Audience_Operator.IsLessThan]: 'Less than',
  [Audience_Operator.IsLessThanOrEqualTo]: 'Less than or',
};

export const targetToTitleMap: Record<Point_Balance_Target, string> = {
  [Point_Balance_Target.Total]: 'ever',
  [Point_Balance_Target.Current]: 'currently',
};

export const membershipCreatedOperatorOptions: [
  {
    label: string;
    value: '';
  },
  ...{
    label: string;
    value: Time_Since_Membership_Acquisition_Operator;
  }[],
] = [
  { label: 'Clear Selection', value: '' },
  { label: 'Exactly', value: Time_Since_Membership_Acquisition_Operator.IsEqualTo },
  { label: 'More than', value: Time_Since_Membership_Acquisition_Operator.IsGreaterThan },
  {
    label: 'More or equal to',
    value: Time_Since_Membership_Acquisition_Operator.IsGreaterThanOrEqualTo,
  },
  { label: 'Less than', value: Time_Since_Membership_Acquisition_Operator.IsLessThan },
  {
    label: 'Less than or equal to',
    value: Time_Since_Membership_Acquisition_Operator.IsLessThanOrEqualTo,
  },
];

export const membershipCreatedInitialFormValues: MembershipCreatedValues = {
  operator: '',
  amount: null,
};

export const membershipCreatedOperatorToTitleMap: Record<
  Time_Since_Membership_Acquisition_Operator,
  string
> = {
  [Time_Since_Membership_Acquisition_Operator.IsEqualTo]: '',
  [Time_Since_Membership_Acquisition_Operator.IsGreaterThan]: 'More than',
  [Time_Since_Membership_Acquisition_Operator.IsGreaterThanOrEqualTo]: 'More than or',
  [Time_Since_Membership_Acquisition_Operator.IsLessThan]: 'Less than',
  [Time_Since_Membership_Acquisition_Operator.IsLessThanOrEqualTo]: 'Less than or',
};

export const employeeStatusTitleMap: Record<Retailer_Network_User_Type, string> = {
  [Retailer_Network_User_Type.FrontLineEmployee]: 'Member is an Employee',
  [Retailer_Network_User_Type.Manager]: 'Member is a Manager',
};

export const employeeStatusTargeting = [
  { label: 'Clear Selection', value: '' },
  {
    label: employeeStatusTitleMap[Retailer_Network_User_Type.FrontLineEmployee],
    value: Retailer_Network_User_Type.FrontLineEmployee,
  },
  {
    label: employeeStatusTitleMap[Retailer_Network_User_Type.Manager],
    value: Retailer_Network_User_Type.Manager,
  },
];

export const retailerNetworkRelationOperatorMap: Record<Retailer_Network_Operator, string> = {
  [Retailer_Network_Operator.InAll]: 'In all',
  [Retailer_Network_Operator.InAny]: 'In any',
  [Retailer_Network_Operator.NotInAll]: 'Not in all',
  [Retailer_Network_Operator.NotInAny]: 'Not in any',
};

export const operatorOptions = [
  {
    label: 'Clear Selection',
    value: '',
  },
  {
    label: retailerNetworkRelationOperatorMap[Retailer_Network_Operator.InAll],
    value: Retailer_Network_Operator.InAll,
  },
  {
    label: retailerNetworkRelationOperatorMap[Retailer_Network_Operator.InAny],
    value: Retailer_Network_Operator.InAny,
  },
  {
    label: retailerNetworkRelationOperatorMap[Retailer_Network_Operator.NotInAll],
    value: Retailer_Network_Operator.NotInAll,
  },
  {
    label: retailerNetworkRelationOperatorMap[Retailer_Network_Operator.NotInAny],
    value: Retailer_Network_Operator.NotInAny,
  },
];
